import React, { useEffect, useState } from "react";
import { Plus, Calendar, Trash2, X, ChevronLeft, ChevronRight } from "lucide-react";
import { ACTIVITY_COLORS, ACTIVITY_LABELS } from "@/lib/api";
import { colorOf } from "@/lib/timesheetUtils";

const ACTIVITIES_FULL = ["MISSION", "SICK", "ABSENCE", "ONBOARDING", "TRAINING", "CONGE"];
const ACTIVITIES_GESTIONNAIRE = ["DAILY", "SICK", "ABSENCE", "TRAINING", "CONGE"];
const ACTIVITIES_FREELANCE = ["MISSION", "CONGE"];

/**
 * DayActionSheet — Iter-115 / Iter-116 mobile UX.
 *
 * Bottom sheet that opens when the user taps a day on mobile. Lets them:
 *   - Switch between activity types (Mission / Sick / Leave / etc.)
 *   - Pick a specific mission when activity = MISSION
 *   - Add 2h or 8h on the chosen activity+mission
 *   - Delete any existing entry on that day
 *   - Navigate to the previous/next day in the SAME month (arrows clamp at month boundaries)
 */
export default function DayActionSheet({
  open, onClose, day, year, month, dateLabel, daysInMonth,
  entries, missions, user, canAdd, onAdd, onDelete, onChangeDay,
}) {
  // Local state: the activity+mission the user picks IN the sheet.
  const isFreelance = !!user?.is_freelance;
  const role = user?.role;
  const activityList = role === "GESTIONNAIRE"
    ? ACTIVITIES_GESTIONNAIRE
    : (isFreelance ? ACTIVITIES_FREELANCE : ACTIVITIES_FULL);
  const [activity, setActivity] = useState(activityList[0]);
  const [missionId, setMissionId] = useState(missions?.[0]?.mission_id || "");
  // Iter-228x-quinto — track in-flight deletions so a fast double-tap on the
  // entry row cannot fire two `DELETE /cra/entries/{id}` requests against the
  // same entry. The second request would otherwise return 404 ("Entry not
  // found") because the first one already deleted the row.
  const [deletingIds, setDeletingIds] = useState(() => new Set());
  const handleDelete = async (entry) => {
    if (deletingIds.has(entry.entry_id)) return;
    setDeletingIds((s) => { const n = new Set(s); n.add(entry.entry_id); return n; });
    try { await onDelete(entry); }
    finally {
      setDeletingIds((s) => { const n = new Set(s); n.delete(entry.entry_id); return n; });
    }
  };

  // Reset selections when the sheet opens or day changes.
  useEffect(() => {
    if (!open) return;
    if (!activityList.includes(activity)) setActivity(activityList[0]);
    if (missions.length > 0 && !missions.find((m) => m.mission_id === missionId)) {
      setMissionId(missions[0].mission_id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, day]);

  if (!open) return null;
  const totalHours = entries.reduce((acc, e) => acc + (Number(e.hours) || 0), 0);
  // Iter-207g — Dynamic day cap. When the user picks MISSION AND every existing
  // entry on that day is also a MISSION, the cap rises to 16h so multiple
  // missions can be stacked on the same calendar day. Otherwise it stays 8h
  // (CONGE/SICK/etc. cannot stack beyond a calendar day; nor can MISSION mix
  // with leave on the same day past 8h total).
  const allExistingAreMission = entries.length === 0 || entries.every((e) => e.activity_type === "MISSION");
  const dayCap = (activity === "MISSION" && allExistingAreMission) ? 16 : 8;
  const remaining = Math.max(0, dayCap - totalHours);
  const needsMission = activity === "MISSION";
  const missionPicked = needsMission ? missionId : true;
  const canAdd2 = canAdd && remaining >= 2 && !!missionPicked;
  const canAdd8 = canAdd && remaining >= 8 && entries.length === 0 && !!missionPicked;

  const activityLabel = (a) => (a === "CONGE" && isFreelance) ? "Leave" : (ACTIVITY_LABELS[a] || a);
  const activityBg = activity === "MISSION"
    ? colorOf(missions.find((m) => m.mission_id === missionId))
    : (ACTIVITY_COLORS[activity] || "#1E3A5F");

  const canPrev = day > 1;
  const canNext = day < daysInMonth;

  return (
    <div
      className="fixed inset-0 z-[10002] flex items-end md:items-center justify-center"
      data-testid="day-action-sheet"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        data-testid="day-action-sheet-backdrop"
      />
      <div className="relative w-full md:w-[440px] bg-[#FAF6EE] rounded-t-2xl md:rounded-2xl shadow-2xl flex flex-col max-h-[88vh] overflow-hidden">
        {/* Drag handle (mobile) */}
        <div className="md:hidden flex justify-center pt-2 pb-1">
          <div className="w-10 h-1 bg-zinc-300 rounded-full" />
        </div>
        {/* Header with prev/next arrows */}
        <div className="px-4 pt-2 pb-3 flex items-center gap-2 border-b border-brand-navy/10">
          <button
            type="button"
            onClick={() => canPrev && onChangeDay(day - 1)}
            disabled={!canPrev}
            data-testid="day-action-sheet-prev"
            className={`w-9 h-9 rounded-md border border-brand-navy/15 inline-flex items-center justify-center shrink-0 ${
              canPrev ? "bg-white hover:bg-brand-navy hover:text-white" : "bg-zinc-50 text-zinc-300 cursor-not-allowed"
            }`}
            title={canPrev ? "Previous day" : "Already first day of the month"}
          >
            <ChevronLeft size={18} />
          </button>
          <div className="flex-1 min-w-0 px-1">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono flex items-center gap-1">
              <Calendar size={10} /> Day
            </div>
            <div className="font-heading font-semibold text-brand-navy text-sm leading-tight" data-testid="day-action-sheet-date">
              {dateLabel}
            </div>
            <div className="text-[11px] text-zinc-500 mt-0.5">
              {totalHours}h / {dayCap}h booked · {remaining}h remaining
              {dayCap === 16 && totalHours >= 8 && (
                <span className="ml-1 text-emerald-700">· multi-mission day</span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => canNext && onChangeDay(day + 1)}
            disabled={!canNext}
            data-testid="day-action-sheet-next"
            className={`w-9 h-9 rounded-md border border-brand-navy/15 inline-flex items-center justify-center shrink-0 ${
              canNext ? "bg-white hover:bg-brand-navy hover:text-white" : "bg-zinc-50 text-zinc-300 cursor-not-allowed"
            }`}
            title={canNext ? "Next day" : "Already last day of the month"}
          >
            <ChevronRight size={18} />
          </button>
          <button
            onClick={onClose}
            data-testid="day-action-sheet-close"
            className="ml-1 p-1.5 rounded hover:bg-zinc-100 text-zinc-500"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body (scrollable) */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {/* Existing entries */}
          {entries.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-2">
                Existing entries{canAdd ? " · tap to delete" : ""}
              </div>
              <div className="space-y-2">
                {entries.map((e) => {
                  const _m = e.activity_type === "MISSION"
                    ? missions.find((mm) => mm.mission_id === e.mission_id)
                    : null;
                  const bg = e.activity_type === "MISSION"
                    ? colorOf(_m || { mission_id: e.mission_id })
                    : (ACTIVITY_COLORS[e.activity_type] || "#1E3A5F");
                  const label = e.activity_type === "MISSION"
                    ? (_m?.title || "Mission")
                    : activityLabel(e.activity_type);
                  return (
                    <button
                      key={e.entry_id}
                      type="button"
                      onClick={() => handleDelete(e)}
                      data-testid={`day-action-sheet-delete-${e.entry_id}`}
                      disabled={!canAdd || deletingIds.has(e.entry_id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border border-zinc-200 bg-white text-left transition-colors ${
                        canAdd && !deletingIds.has(e.entry_id) ? "hover:bg-red-50 hover:border-red-200" : "opacity-60 cursor-not-allowed"
                      }`}
                    >
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: bg }} />
                      <span className="flex-1 min-w-0 truncate text-sm text-brand-ink font-medium">{label}</span>
                      <span className="text-xs font-mono text-zinc-500">{e.hours}h</span>
                      {canAdd && <Trash2 size={15} className="text-red-500 shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {canAdd && (
            <>
              {/* Activity picker */}
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-2">Activity</div>
                <div className="flex flex-wrap gap-1.5">
                  {activityList.map((a) => {
                    const isSel = activity === a;
                    const bg = ACTIVITY_COLORS[a] || "#1E3A5F";
                    return (
                      <button
                        key={a}
                        type="button"
                        onClick={() => setActivity(a)}
                        data-testid={`day-action-sheet-activity-${a}`}
                        className={`px-2.5 h-8 rounded-md text-xs font-medium inline-flex items-center gap-1.5 transition-colors border ${
                          isSel ? "text-white border-transparent" : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
                        }`}
                        style={isSel ? { background: bg } : {}}
                      >
                        <span
                          className="w-1.5 h-1.5 rounded-full"
                          style={{ background: isSel ? "#fff" : bg }}
                        />
                        {activityLabel(a)}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Mission picker (only when activity = MISSION) */}
              {needsMission && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-2">Mission</div>
                  {missions.length === 0 ? (
                    <div className="text-xs text-zinc-500 italic px-3 py-2 bg-white border border-zinc-200 rounded-md">
                      No mission assigned for this period.
                    </div>
                  ) : (
                    <div className="space-y-1.5 max-h-44 overflow-y-auto pr-1">
                      {missions.map((m) => {
                        const isSel = missionId === m.mission_id;
                        const c = colorOf(m);
                        const env = Number(m.envelope_jh || 0);
                        const cons = Number(m.consumed_jh || 0);
                        // Iter-215 — show "remaining/envelope" instead of "consumed/envelope".
                        const remaining = env > 0 ? Math.max(0, env - cons) : 0;
                        const counter = env > 0
                          ? `${remaining.toLocaleString(undefined, { maximumFractionDigits: 1 })}/${env} j`
                          : null;
                        return (
                          <button
                            key={m.mission_id}
                            type="button"
                            onClick={() => setMissionId(m.mission_id)}
                            data-testid={`day-action-sheet-mission-${m.mission_id}`}
                            className={`w-full flex items-center gap-2.5 p-2.5 rounded-md border text-left transition-colors ${
                              isSel
                                ? "border-transparent text-white"
                                : "bg-white border-zinc-200 text-zinc-700 hover:bg-zinc-50"
                            }`}
                            style={isSel ? { background: c } : {}}
                          >
                            <span
                              className="w-2 h-2 rounded-full shrink-0"
                              style={{ background: isSel ? "#fff" : c }}
                            />
                            <span className="flex-1 min-w-0 truncate text-sm font-medium">{m.title}</span>
                            {counter && (
                              <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
                                isSel ? "bg-white/25 text-white" : "bg-zinc-100 text-zinc-600"
                              }`}>
                                {counter}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Add buttons */}
              <div>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => onAdd(2, activity, needsMission ? missionId : null)}
                    disabled={!canAdd2}
                    data-testid="day-action-sheet-add-2h"
                    className={`flex flex-col items-center justify-center py-4 rounded-lg border transition-colors ${
                      canAdd2
                        ? "border-brand-navy/30 bg-white hover:bg-brand-navy hover:text-white text-brand-navy"
                        : "border-zinc-200 bg-zinc-50 text-zinc-400 cursor-not-allowed"
                    }`}
                    style={canAdd2 ? { borderLeft: `4px solid ${activityBg}` } : {}}
                  >
                    <Plus size={20} strokeWidth={2.25} />
                    <span className="font-heading font-bold text-lg mt-1">+2h</span>
                    <span className="text-[10px] uppercase tracking-wider opacity-70">Quart-jour</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => onAdd(8, activity, needsMission ? missionId : null)}
                    disabled={!canAdd8}
                    data-testid="day-action-sheet-add-8h"
                    className={`flex flex-col items-center justify-center py-4 rounded-lg border transition-colors ${
                      canAdd8
                        ? "border-brand-navy/30 bg-white hover:bg-brand-navy hover:text-white text-brand-navy"
                        : "border-zinc-200 bg-zinc-50 text-zinc-400 cursor-not-allowed"
                    }`}
                    style={canAdd8 ? { borderLeft: `4px solid ${activityBg}` } : {}}
                  >
                    <Plus size={20} strokeWidth={2.25} />
                    <span className="font-heading font-bold text-lg mt-1">+8h</span>
                    <span className="text-[10px] uppercase tracking-wider opacity-70">Full day</span>
                  </button>
                </div>
                {needsMission && !missionPicked && (
                  <div className="text-[11px] text-amber-700 mt-2 text-center">
                    Pick a mission first.
                  </div>
                )}
                {!canAdd2 && missionPicked && entries.length > 0 && remaining < 2 && (
                  <div className="text-[11px] text-zinc-500 mt-2 text-center">
                    Day is full ({dayCap}h reached). {dayCap === 8 && allExistingAreMission && (
                      <span>Pick MISSION to allow up to 16h stacking.</span>
                    )}
                    {dayCap === 8 && !allExistingAreMission && (
                      <span>Non-mission entries cap the day at 8h.</span>
                    )}
                  </div>
                )}
                {!canAdd8 && canAdd2 && entries.length > 0 && (
                  <div className="text-[11px] text-zinc-500 mt-2 text-center">
                    +8h disabled because the day already has imputations.
                  </div>
                )}
              </div>
            </>
          )}

          {!canAdd && (
            <div className="text-sm text-zinc-500 text-center py-4">
              Month locked — cannot edit imputations.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
